import { CommonModule } from '@angular/common';
import { Component, effect, inject, model, ChangeDetectionStrategy } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { AiAssistant, AiAssistantService, DEFAULT_AVATAR_SVG_ICON } from '../../core';
import { NAMESPACE_CONFIG_TOKEN, SHOW_CUSTOM_ASSISTANT_TOKEN } from '../../core/tokens';
import { combineLatest, map } from 'rxjs';
import { AssistantType } from '@vendasta/protos/ai_assistants/v1alpha1';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { TranslateModule } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { aiAssistantAssetUrl } from '../../core/pipes/ai-assistant-asset-url.pipe';
import { DOCUMENT } from '@angular/common';

export interface DialogData {
  activeAssistantId: string;
}

@Component({
  selector: 'ai-assistant-select-dialog',
  imports: [
    CommonModule,
    MatButtonModule,
    GalaxyAvatarModule,
    TranslateModule,
    MatIconModule,
    MatDialogModule,
    GalaxyLoadingSpinnerModule,
  ],
  templateUrl: './ai-assistant-select-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./ai-assistant-select-dialog.component.scss'],
})
export class AIAssistantSelectDialogComponent {
  readonly dialogRef = inject(MatDialogRef<AIAssistantSelectDialogComponent>);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  private assistantService = inject(AiAssistantService);
  protected readonly aiIconService = inject(GalaxyAiIconService);

  protected readonly defaultAvatarIcon = DEFAULT_AVATAR_SVG_ICON;
  private readonly document = inject(DOCUMENT);
  private readonly namespaceConfig = toSignal(inject(NAMESPACE_CONFIG_TOKEN));
  private readonly defaultWorkforce = toSignal(this.assistantService.defaultWorkforce$);
  private readonly showCustomAssistant$ = inject(SHOW_CUSTOM_ASSISTANT_TOKEN);

  readonly assistantResource = rxResource({
    params: () => {
      if (!this.namespaceConfig()?.namespace) return;
      return {
        namespace: this.namespaceConfig()?.namespace,
        defaultWorkforce: this.defaultWorkforce() || [],
      };
    },
    stream: ({ params }) =>
      combineLatest([this.assistantService.listAllAssistants(params.namespace!), this.showCustomAssistant$]).pipe(
        map(([res, showCustomAssistant]) => {
          const filtered = res
            .filter((a) => {
              if (a.type === AssistantType.ASSISTANT_TYPE_INBOX) {
                return true;
              }
              if (a.type === AssistantType.ASSISTANT_TYPE_CUSTOM) {
                return showCustomAssistant;
              }
              return false;
            })
            .map(
              (assistant) =>
                this.assistantService.hydrateAssistantWithDefaultInfo(assistant, params.defaultWorkforce) ||
                ({} as AiAssistant),
            );
          return filtered;
        }),
      ),
  });

  readonly selectedAssistant = model<AiAssistant | null>(null);

  constructor() {
    effect(() => {
      const activeId = this.data.activeAssistantId;
      const assistants = this.assistantResource.value();
      this.selectedAssistant.set(assistants?.find((a) => a.assistant.id === activeId) || null);
    });
  }

  selectAssistant(assistant: AiAssistant) {
    this.selectedAssistant.set(assistant || null);
  }

  protected getAvatarSrc(assistant: AiAssistant): string | null {
    return (
      assistant.assistant.avatarUrl ||
      assistant.decoration?.avatarIconUrl ||
      aiAssistantAssetUrl(assistant.decoration?.defaultAvatarIcon, this.document)
    );
  }
}
